// src/components/PdfWhiteboard/PdfControls.js
//
// This component provides the top control bar for the Replan PDF viewer.
// Features include:
// - Project and version selection
// - Page navigation (prev/next, dropdown)
// - Scale selection (metric/imperial)
// - Undo/Redo controls
// - Save functionality
// - OCR-based text search with highlighting
//
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { SERVER_URL } from '../../../DB_Url';
import { METRIC_SCALES, IMPERIAL_SCALES } from '../constants/scales';
import vcontrolAPI from '../../../services/documentApi';
import PageSelectorModal, {
  clearThumbnailCache,
} from './PageSelectorModal';
import ShortcutReferenceModal from './ShortcutReferenceModal';

const PdfControls = ({
  pdfDoc,
  stageScale,
  currentPage,
  numPages,
  pages,
  onPageChange,
  onResetView,
  mousePos,
  projectName,
  versionName,
  onProjectChange,
  onVersionChange,
  onFolderChange,
  selectedFolder,
  measurementSystem,
  onMeasurementSystemChange,
  selectedScaleKey,
  onSelectedScaleKeyChange,
  onSave,
  saveStatus,
  onDiscardDraft,
  showDiscardDraft,
  discardDraftStatus,
  onPrint, // Add onPrint prop
  authUser,
  firebase, // Add firebase prop
  customScales = [],
  setCustomScales,
  // UndoRedo bar
  undo,
  redo,
  canUndo,
  canRedo,
  // Search functionality
  onSearchResults,
  // Pages with shapes for thumbnail badges
  pagesWithShapes,
  displayKeys,
  displayUnitSystem,
  overlayTintColor,
  onOverlayTintChange,
  overlayModeEnabled,
  onOverlayModeChange,
  overlayFilterEnabled,
  onOverlayFilterChange,
  isOverlayOnTop,
  onOverlayOrderChange,
  selectedVersionShapes = [],
  onVersionShapesChange,
  onSavedPageRestoreComplete,
  hasDocuments = true,
}) => {
  const { t } = useTranslation();
  // When the shared project selector reports this project has no uploaded
  // documents, the toolbar's controls are visually and interactively inert.
  const controlsDisabled = hasDocuments === false;
  const [projects, setProjects] = useState([]);
  const [allVersions, setAllVersions] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] =
    useState(false);
  const [isScaleDropdownOpen, setIsScaleDropdownOpen] =
    useState(false);
  const [selectedAlternative, setSelectedAlternative] = useState('');
  const [hasLoadedSavedPage, setHasLoadedSavedPage] = useState(false);
  const [localOverlayTint, setLocalOverlayTint] = useState(
    overlayTintColor || '#ff7800',
  );
  const [basePageVersion, setBasePageVersion] = useState('');
  const baseVersionByPageRef = useRef(new Map());

  // Custom scales state
  const [customScaleRatio, setCustomScaleRatio] = useState('');
  const [showCustomScaleUI, setShowCustomScaleUI] = useState(false);

  // --- OCR Search State ---
  // searchQuery: Current search input text
  // searchResults: Array of match objects from OCR API (each has position: {x, y, width, height})
  // currentMatchIndex: Index of currently highlighted match (for cycling with Enter/arrows)
  // isSearching: Loading state while API request is in progress
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  // Page selector modal state
  const [isPageSelectorOpen, setIsPageSelectorOpen] = useState(false);
  const [isShortcutModalOpen, setIsShortcutModalOpen] = useState(false);

  const searchInputRef = useRef(null);
  const prevPageRef = useRef(null);
  const nextPageRef = useRef(null);
  const onSaveRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        setIsPageSelectorOpen((prev) => !prev);
        return;
      }
      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          prevPageRef.current?.();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          nextPageRef.current?.();
        } else if (event.key === 'f') {
          event.preventDefault();
          searchInputRef.current?.focus();
        } else if (event.key === 's') {
          event.preventDefault();
          onSaveRef.current?.();
        }
      }
      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const tag = (event.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        event.preventDefault();
        setIsShortcutModalOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Folder selection state
  const [availableFolders, setAvailableFolders] = useState([]);

  // Refs for dropdown positioning
  const versionSelectorRef = useRef(null);
  const versionDropdownRef = useRef(null);
  const scaleSelectorRef = useRef(null);
  const scaleDropdownRef = useRef(null);
  const [versionDropdownPos, setVersionDropdownPos] = useState({
    top: 0,
    left: 0,
  });
  const [scaleDropdownPos, setScaleDropdownPos] = useState({
    top: 0,
    left: 0,
  });

  const currentPageData = pages?.[currentPage - 1];
  const baseScaleList =
    measurementSystem === 'metric' ? METRIC_SCALES : IMPERIAL_SCALES;
  const currentScaleList = [
    ...baseScaleList,
    ...customScales.filter(
      (cs) =>
        cs.documentUnit ===
        (measurementSystem === 'metric' ? 'mm' : 'in'),
    ),
  ];
  const selectedScaleLabel =
    currentScaleList.find((scale) => scale.key === selectedScaleKey)
      ?.label || '';
  const selectedAltVersion = selectedAlternative.split('|')[0];
  const isOutdatedVersion =
    selectedAltVersion &&
    basePageVersion &&
    selectedAltVersion !== basePageVersion;

  // Function to save user's project selection to Firebase Realtime Database
  const saveSelectedProjectToProfile = useCallback(
    async (projectName) => {
      if (!authUser || !authUser.uid || !firebase) {
        console.log(
          'Missing required data for saving project to Replan profile',
        );
        return;
      }

      try {
        console.log('Saving Replan project to profile:', projectName);
        await firebase.doSetUserPreference(
          authUser.uid,
          'replan/selectedProject',
          projectName,
        );
        console.log('Replan project saved successfully');
      } catch (error) {
        console.error(
          'Failed to save selected Replan project to profile:',
          error,
        );
      }
    },
    [authUser, firebase],
  );

  // Function to load user's saved project selection from Firebase Realtime Database
  const loadSelectedProjectFromProfile = useCallback(async () => {
    if (!authUser || !authUser.uid || !firebase) {
      console.log(
        'Cannot load Replan project - missing auth or firebase',
      );
      return null;
    }

    try {
      console.log('Loading saved Replan project from profile');
      const savedProject = await firebase.doGetUserPreference(
        authUser.uid,
        'replan/selectedProject',
      );
      console.log('Loaded saved Replan project:', savedProject);
      return savedProject;
    } catch (error) {
      console.error(
        'Failed to load selected Replan project from profile:',
        error,
      );
      return null;
    }
  }, [authUser, firebase]);

  // Function to save user's page selection to Firebase Realtime Database
  const saveSelectedPageToProfile = useCallback(
    async (projectName, pageIndex) => {
      if (!authUser || !authUser.uid || !firebase || !projectName) {
        return;
      }

      try {
        console.log('Saving Replan page to profile:', {
          projectName,
          pageIndex,
        });
        await firebase.doSetUserPreference(
          authUser.uid,
          `replan/selectedPage/${projectName}`,
          pageIndex,
        );
        console.log('Replan page saved successfully');
      } catch (error) {
        console.error(
          'Failed to save selected Replan page to profile:',
          error,
        );
      }
    },
    [authUser, firebase],
  );

  // Function to load user's saved page selection from Firebase Realtime Database
  const loadSelectedPageFromProfile = useCallback(
    async (projectName) => {
      if (!authUser || !authUser.uid || !firebase || !projectName) {
        return null;
      }

      try {
        console.log(
          'Loading saved Replan page from profile for project:',
          projectName,
        );
        const savedPage = await firebase.doGetUserPreference(
          authUser.uid,
          `replan/selectedPage/${projectName}`,
        );
        console.log('Loaded saved Replan page:', savedPage);
        return savedPage;
      } catch (error) {
        console.error(
          'Failed to load selected Replan page from profile:',
          error,
        );
        return null;
      }
    },
    [authUser, firebase],
  );

  const handleAddCustomScale = async () => {
    if (!customScaleRatio || customScaleRatio < 1) {
      alert('Please enter a valid ratio');
      return;
    }

    const documentUnit = measurementSystem === 'metric' ? 'mm' : 'in';
    const newScale = {
      key: `custom_${Date.now()}`,
      label: `1:${customScaleRatio}`,
      ratio: parseInt(customScaleRatio, 10),
      documentUnit,
    };

    const updated = [...customScales, newScale];
    setCustomScales(updated);

    // Save to Firebase
    if (authUser && firebase) {
      firebase
        .doSetUserPreference(authUser.uid, 'customScales', updated)
        .catch((err) =>
          console.error('Error saving custom scale:', err),
        );
    }

    // Select the newly created scale
    onSelectedScaleKeyChange(newScale.key);

    // Clear inputs
    setCustomScaleRatio('');
  };

  const handleDeleteCustomScale = async (scaleKey) => {
    const updated = customScales.filter((s) => s.key !== scaleKey);
    setCustomScales(updated);

    // Save to Firebase
    if (authUser && firebase) {
      firebase
        .doSetUserPreference(authUser.uid, 'customScales', updated)
        .catch((err) =>
          console.error('Error deleting custom scale:', err),
        );
    }
  };

  useEffect(() => {
    if (!authUser || !authUser.roles) {
      setProjects([]);
      return;
    }
    const fetchProjects = async () => {
      try {
        const userOrgs = Object.keys(authUser.roles).filter(
          (key) => authUser.roles[key] !== null,
        );
        if (userOrgs.length === 0) {
          setProjects([]);
          return;
        }
        const projectsUrl = new URL(`${SERVER_URL}/api/projects`);
        projectsUrl.searchParams.append(
          'organisations',
          userOrgs.join(','),
        );
        const response = await fetch(projectsUrl);
        if (response.ok) {
          const data = await response.json();
          setProjects(data);

          // Load saved project selection after projects are fetched
          // Only auto-load if no project is currently selected (respects URL params)
          if (firebase && !projectName) {
            const savedProject =
              await loadSelectedProjectFromProfile();
            if (savedProject && data.includes(savedProject)) {
              console.log(
                'Auto-selecting saved Replan project:',
                savedProject,
              );
              // Use a small delay to ensure URL sync has completed initialization
              setTimeout(() => {
                // Double-check that no project was loaded from URL in the meantime
                if (!projectName) {
                  onProjectChange(savedProject);
                }
              }, 150);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    fetchProjects();
  }, [
    authUser,
    firebase,
    loadSelectedProjectFromProfile,
    onProjectChange,
    projectName,
  ]);

  // Load saved page when project and pages are available
  useEffect(() => {
    const loadSavedPage = async () => {
      if (
        projectName &&
        authUser?.uid &&
        pages &&
        pages.length > 0 &&
        firebase &&
        !hasLoadedSavedPage
      ) {
        // A URL page param only represents a deliberate deep link when it
        // belongs to the current project AND resolves to an actual page here.
        // A stale param from a previously viewed project, or a param that no
        // longer matches a page, must not suppress the per-project restore —
        // that is exactly what caused the wrong page after switching.
        const urlParams = new URLSearchParams(window.location.search);
        const urlProject = urlParams.get('project');
        const urlPage = urlParams.get('page');
        const urlPageExists =
          urlPage &&
          pages.some(
            (page) => page.pageLabel === urlPage || page.name === urlPage,
          );

        if (urlPage && urlProject === projectName && urlPageExists) {
          setHasLoadedSavedPage(true);
          onSavedPageRestoreComplete?.();
          return;
        }

        const savedPage = await loadSelectedPageFromProfile(projectName);
        if (savedPage && savedPage !== currentPage) {
          if (typeof savedPage === 'number') {
            // Legacy format: a numeric position in the pages array.
            const pageExists = pages.some(
              (page) => page.pageIndex + 1 === savedPage,
            );
            if (pageExists) {
              console.log(
                'Auto-selecting saved Replan page:',
                savedPage,
              );
              onPageChange(savedPage);
            }
          } else if (typeof savedPage === 'string') {
            // Current format: a stable page identity (pageLabel/name) that
            // survives filter changes and page additions/removals.
            const targetIndex = pages.findIndex(
              (page) =>
                page.pageLabel === savedPage || page.name === savedPage,
            );
            if (targetIndex !== -1 && targetIndex + 1 !== currentPage) {
              console.log(
                'Auto-selecting saved Replan page:',
                savedPage,
              );
              onPageChange(targetIndex + 1);
            }
          }
        }
        setHasLoadedSavedPage(true);
        onSavedPageRestoreComplete?.();
      }
    };
    loadSavedPage();
  }, [
    projectName,
    authUser?.uid,
    pages,
    firebase,
    hasLoadedSavedPage,
    currentPage,
    onPageChange,
    loadSelectedPageFromProfile,
    onSavedPageRestoreComplete,
  ]);

  // Reset hasLoadedSavedPage when project changes
  useEffect(() => {
    setHasLoadedSavedPage(false);
  }, [projectName]);

  const [allProjectPages, setAllProjectPages] = useState([]);
  const [projectPagesLoading, setProjectPagesLoading] = useState(false);
  const [projectPagesError, setProjectPagesError] = useState(null);
  const [projectPagesReloadToken, setProjectPagesReloadToken] =
    useState(0);

  // Fetch all available folders and pages for the project (unfiltered by current
  // folder/version). This is the page list the PageSelectorModal grid shows, so it
  // must be resilient: getDocumentInfo goes through fetchWithRetry (auth bearer
  // token + 401 refresh + retries on transient 5xx/network errors), unlike the
  // previous bare fetch which silently left the modal empty on any failure. The
  // `cancelled` flag ignores stale responses when the project changes or a retry
  // supersedes the request.
  useEffect(() => {
    if (!projectName || hasDocuments === false) {
      setAvailableFolders([]);
      setAllProjectPages([]);
      setProjectPagesLoading(false);
      setProjectPagesError(null);
      return;
    }

    let cancelled = false;

    const fetchProjectStructure = async () => {
      setProjectPagesLoading(true);
      setProjectPagesError(null);
      try {
        // We intentionally fetch info for the whole project without folder filters
        // to populate the folder selector and the modal grid with all options.
        const projectPages = await vcontrolAPI.getDocumentInfo(projectName);
        if (cancelled) return;
        setAllProjectPages(projectPages);

        const folders = new Set();
        projectPages.forEach((page) => {
          if (page.folderPath) {
            folders.add(page.folderPath);
          }
        });
        setAvailableFolders(Array.from(folders).sort());
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch project structure:', error);
        setProjectPagesError(
          error.message || 'Failed to load project pages',
        );
      } finally {
        if (!cancelled) {
          setProjectPagesLoading(false);
        }
      }
    };

    fetchProjectStructure();

    return () => {
      cancelled = true;
    };
  }, [projectName, hasDocuments, projectPagesReloadToken]);

  // Refresh the page list when the modal opens if the previous attempt left it
  // empty or failed. A transient failure must not poison the modal for the rest
  // of the session — closing and reopening it retries automatically.
  const prevModalOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isPageSelectorOpen && !prevModalOpenRef.current;
    prevModalOpenRef.current = isPageSelectorOpen;
    if (
      justOpened &&
      hasDocuments !== false &&
      (allProjectPages.length === 0 || projectPagesError)
    ) {
      setProjectPagesReloadToken((token) => token + 1);
    }
  }, [
    isPageSelectorOpen,
    hasDocuments,
    allProjectPages.length,
    projectPagesError,
  ]);

  useEffect(() => {
    if (currentPageData) {
      const currentAlternativeValue = `${currentPageData.version}|${currentPageData.documentId}|${currentPageData.actualPageNumber}`;
      setSelectedAlternative(currentAlternativeValue);

      const pageKey =
        currentPageData.pageLabel ||
        currentPageData.name ||
        String(currentPageData.pageIndex ?? '');
      const baseMap = baseVersionByPageRef.current;
      if (!baseMap.has(pageKey)) {
        baseMap.set(pageKey, currentPageData.version);
      }
      setBasePageVersion(
        baseMap.get(pageKey) || currentPageData.version,
      );
    }
  }, [currentPageData]);

  useEffect(() => {
    if (overlayTintColor) {
      setLocalOverlayTint(overlayTintColor);
    }
  }, [overlayTintColor]);

  useEffect(() => {
    if (!projectName) {
      setAllVersions([]);
      setSelectedVersions([]);
      return;
    }
    const fetchVersions = async () => {
      try {
        const versionsUrl = new URL(`${SERVER_URL}/api/versions`);
        versionsUrl.searchParams.append('project', projectName);
        const response = await fetch(versionsUrl);
        if (response.ok) {
          const data = await response.json();
          setAllVersions(data);
          setSelectedVersions(data);
          onVersionChange('');
        } else {
          setAllVersions([]);
          setSelectedVersions([]);
        }
      } catch (error) {
        console.error(
          `Failed to fetch versions for ${projectName}:`,
          error,
        );
        setAllVersions([]);
        setSelectedVersions([]);
      }
    };
    fetchVersions();
  }, [projectName]);

  const handleProjectSelect = async (e) => {
    const newProject = e.target.value;
    console.log('Replan project selected:', newProject);
    onProjectChange(newProject);
    onVersionChange('');

    // Clear thumbnail cache when switching projects to free memory
    clearThumbnailCache();

    // Save the selected project to user profile
    if (newProject) {
      await saveSelectedProjectToProfile(newProject);
    }
  };

  /**
   * Handle page navigation
   * Saves the new page to user profile and clears any active search results
   * @param {number} newPage - The page number to navigate to (1-indexed)
   */
  const handlePageChange = async (newPage) => {
    console.log('Replan page selected:', newPage);
    onPageChange(newPage);

    // Save the selected page to user profile. Prefer a stable page identity
    // (pageLabel/name) over the numeric position: positions shift when pages
    // are added/removed or when folder/version filters change between sessions,
    // which made the restore land on the wrong physical page.
    if (projectName) {
      const targetPage =
        pages?.find((page) => page.pageIndex + 1 === newPage) ||
        pages?.[newPage - 1];
      const pageIdentity =
        targetPage?.pageLabel || targetPage?.name || newPage;
      await saveSelectedPageToProfile(projectName, pageIdentity);
    }

    // Clear search results when page changes
    setSearchQuery('');
    setSearchResults([]);
    setCurrentMatchIndex(0);
    if (onSearchResults) {
      onSearchResults([], 0);
    }
  };

  /**
   * Perform OCR text search on current page
   * Calls the backend API to search for text matches using OCR data
   * @param {string} query - Search query (minimum 2 characters)
   */
  const handleSearch = useCallback(
    async (query) => {
      // Clear results if query is too short
      if (!query || query.length < 2) {
        setSearchResults([]);
        setCurrentMatchIndex(0);
        if (onSearchResults) {
          onSearchResults([], 0);
        }
        return;
      }

      // Ensure we have valid page data
      if (!currentPageData || !currentPageData.documentId) {
        return;
      }

      setIsSearching(true);
      try {
        // Call OCR search API for current page
        const results = await vcontrolAPI.searchPageText(
          currentPageData.documentId,
          currentPageData.actualPageNumber,
          query,
        );

        // Extract matches array from API response
        // Each match has: {text, confidence, position: {x, y, width, height}}
        const matches = results.matches || [];
        setSearchResults(matches);
        setCurrentMatchIndex(0);

        // Notify parent component (Replan) of results for highlighting
        if (onSearchResults) {
          onSearchResults(matches, 0);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
        setCurrentMatchIndex(0);
      } finally {
        setIsSearching(false);
      }
    },
    [currentPageData, onSearchResults],
  );

  /**
   * Handle Enter key in search input
   * - If no results exist, perform search
   * - If results exist, navigate to next match
   */
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      // If we have results, navigate to next; otherwise, perform search
      if (searchResults.length > 0) {
        handleNextMatch();
      } else {
        handleSearch(searchQuery);
      }
    }
  };

  /**
   * Navigate to next search match (cycles back to first after last)
   */
  const handleNextMatch = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % searchResults.length;
    setCurrentMatchIndex(nextIndex);
    if (onSearchResults) {
      onSearchResults(searchResults, nextIndex);
    }
  };

  /**
   * Navigate to previous search match (cycles to last after first)
   */
  const handlePrevMatch = () => {
    if (searchResults.length === 0) return;
    const prevIndex =
      (currentMatchIndex - 1 + searchResults.length) %
      searchResults.length;
    setCurrentMatchIndex(prevIndex);
    if (onSearchResults) {
      onSearchResults(searchResults, prevIndex);
    }
  };

  const handleAlternativeVersionSelect = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) return;

    setSelectedAlternative(selectedValue);

    const [version, documentId, actualPageNumberStr] =
      selectedValue.split('|');
    const actualPageNumber = parseInt(actualPageNumberStr, 10);

    let nextSelectedVersions = [
      ...new Set([...selectedVersions, version]),
    ];
    let excluded = allVersions.filter(
      (v) => !nextSelectedVersions.includes(v),
    );

    if (!overlayModeEnabled) {
      nextSelectedVersions = [version];
      excluded = allVersions.filter((v) => v !== version);
    }

    setSelectedVersions(nextSelectedVersions);
    onVersionChange(excluded.join(','), {
      navigateTo: { documentId, actualPageNumber },
      mode: overlayModeEnabled ? 'overlay' : 'page',
    });

    // Reset view when switching versions/pages via the dropdown
    onResetView();
  };

  const handleVersionSelect = (e) => {
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      (option) => option.value,
    );
    setSelectedVersions(selectedOptions);
    const excludedVersions = allVersions.filter(
      (version) => !selectedOptions.includes(version),
    );
    onVersionChange(excludedVersions.join(','));
  };

  /**
   * Calculate and update the position for version dropdown
   */
  const updateVersionDropdownPosition = () => {
    if (versionSelectorRef.current) {
      const rect = versionSelectorRef.current.getBoundingClientRect();
      setVersionDropdownPos({
        top: rect.bottom + 5,
        left: rect.left,
      });
    }
  };

  /**
   * Calculate and update the position for scale dropdown
   */
  const updateScaleDropdownPosition = () => {
    if (scaleSelectorRef.current) {
      const rect = scaleSelectorRef.current.getBoundingClientRect();
      setScaleDropdownPos({
        top: rect.bottom + 5,
        left: rect.left,
      });
    }
  };

  const toggleVersionDropdown = () => {
    if (!isVersionDropdownOpen) {
      setTimeout(updateVersionDropdownPosition, 0);
    }
    setIsVersionDropdownOpen(!isVersionDropdownOpen);
  };

  const handleVersionClose = () => setIsVersionDropdownOpen(false);

  const toggleScaleDropdown = () => {
    if (!isScaleDropdownOpen) {
      setTimeout(updateScaleDropdownPosition, 0);
    }
    setIsScaleDropdownOpen(!isScaleDropdownOpen);
  };

  const handleScaleSelect = (system, key) => {
    onMeasurementSystemChange(system);
    onSelectedScaleKeyChange(key);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isVersionDropdownOpen &&
        !event.target.closest('.version-selector') &&
        !(
          versionDropdownRef.current &&
          versionDropdownRef.current.contains(event.target)
        )
      ) {
        setIsVersionDropdownOpen(false);
      }
      if (
        isScaleDropdownOpen &&
        !event.target.closest('.scale-selector') &&
        !(
          scaleDropdownRef.current &&
          scaleDropdownRef.current.contains(event.target)
        )
      ) {
        setIsScaleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, [isVersionDropdownOpen, isScaleDropdownOpen]);

  // Update dropdown positions when they open or window resizes
  useEffect(() => {
    if (isVersionDropdownOpen) {
      updateVersionDropdownPosition();
      window.addEventListener(
        'scroll',
        updateVersionDropdownPosition,
      );
      window.addEventListener(
        'resize',
        updateVersionDropdownPosition,
      );
      return () => {
        window.removeEventListener(
          'scroll',
          updateVersionDropdownPosition,
        );
        window.removeEventListener(
          'resize',
          updateVersionDropdownPosition,
        );
      };
    }
  }, [isVersionDropdownOpen]);

  useEffect(() => {
    if (isScaleDropdownOpen) {
      updateScaleDropdownPosition();
      window.addEventListener('scroll', updateScaleDropdownPosition);
      window.addEventListener('resize', updateScaleDropdownPosition);
      return () => {
        window.removeEventListener(
          'scroll',
          updateScaleDropdownPosition,
        );
        window.removeEventListener(
          'resize',
          updateScaleDropdownPosition,
        );
      };
    }
  }, [isScaleDropdownOpen]);

  const handleFolderSelect = (e) => {
    const folder = e.target.value;
    if (onFolderChange) {
      onFolderChange(folder);
    }
  };

  const prev_page = () => {
    const newPage = Math.max(1, currentPage - 1);
    handlePageChange(newPage);
  };

  const next_page = () => {
    const newPage = Math.min(numPages, currentPage + 1);
    handlePageChange(newPage);
  };

  prevPageRef.current = prev_page;
  nextPageRef.current = next_page;
  onSaveRef.current = onSave;

  return (
    <div className="pdftoolbar">
      {/* <select
        value={selectedFolder}
        onChange={handleFolderSelect}
        data-tt={
          t('replan.pdfControls.select_folder') || 'Select Folder'
        }
        className="folder-selector"
      >
        <option value="">
          {t('replan.pdfControls.all_folders') || 'All Folders'}
        </option>
        {availableFolders.map((folder) => (
          <option key={folder} value={folder}>
            {folder}
          </option>
        ))}
      </select> */}
      <button
        onClick={() => setIsPageSelectorOpen(true)}
        disabled={controlsDisabled}
        data-tt={t('replan.pdfControls.open_page_selector', {
          pageLabel: (currentPageData?.pageLabel || `${t('replan.pdfControls.page')} ${currentPage}`).replace(/\n/g, ' '),
          defaultValue: 'Browse all pages in the project\nCurrent: {{pageLabel}}\nCtrl+Enter to open',
        })}
        className="page-selector-btn"
      >
        {currentPageData?.pageLabel ||
          `${t('replan.pdfControls.page')} ${currentPage}`}
      </button>
      <div className="version-selector" ref={versionSelectorRef}>
        <button
          type="button"
          onClick={toggleVersionDropdown}
          disabled={controlsDisabled}
          className={`dropdown-trigger${isOutdatedVersion ? ' dropdown-trigger-outdated' : ''
            }`}
          data-tt={t("replan.pdfControls.select_versions", {
            version: selectedAlternative ? selectedAlternative.split("|")[0] : t("replan.pdfControls.none"),
            defaultValue: "Select versions to display\nActive: {{version}}\nClick to expand list",
          })}
        >
          {selectedAlternative.split('|')[0]} ▼
          {/* ({selectedVersions.length}/{allVersions.length}) ▼ */}
        </button>

        {(isVersionDropdownOpen || overlayModeEnabled) &&
          createPortal(
            <div
              ref={versionDropdownRef}
              className="version-dropdown-content"
              style={{
                top: `${versionDropdownPos.top}px`,
                left: `${versionDropdownPos.left}px`,
              }}
            >
              <div className="scale-switch version-mode-switch">
                <button
                  type="button"
                  className={`switch-btn ${!overlayModeEnabled ? 'active' : ''}`}
                  onClick={() => {
                    if (onOverlayModeChange) {
                      onOverlayModeChange(false);
                    }
                    // Reset select to initial value (current page)
                    if (currentPageData) {
                      const initialValue = `${currentPageData.version}|${currentPageData.documentId}|${currentPageData.actualPageNumber}`;
                      setSelectedAlternative(initialValue);
                    }
                  }}
                  data-tt={t('replan.pdfControls.normal_mode_desc', {
                    defaultValue: 'Show only one version at a time\nCurrent mode: Normal\nClick to switch to Overlay mode',
                  })}
                >
                  {t('replan.pdfControls.normal_mode') ||
                    'Normal mode'}
                </button>
                <button
                  type="button"
                  className={`switch-btn ${overlayModeEnabled ? 'active' : ''}`}
                  onClick={() => {
                    if (onOverlayModeChange) {
                      onOverlayModeChange(true);
                    }
                    // Reset select to initial value (current page)
                    if (currentPageData) {
                      const initialValue = `${currentPageData.version}|${currentPageData.documentId}|${currentPageData.actualPageNumber}`;
                      setSelectedAlternative(initialValue);
                    }
                  }}
                  data-tt={t('replan.pdfControls.overlay_mode_desc', {
                    defaultValue: 'Show multiple versions stacked together\nCurrent mode: Overlay\nClick to switch to Normal mode',
                  })}
                >
                  {t('replan.pdfControls.overlay_mode') ||
                    'Overlay mode'}
                </button>
              </div>
              <label htmlFor="Older versions">
                {/* {t('replan.pdfControls.older_versions')} */}
                {currentPageData &&
                  currentPageData.alternativeVersions &&
                  currentPageData.alternativeVersions.length > 1 && (
                    <select
                      className="alt-version-select"
                      onChange={handleAlternativeVersionSelect}
                      value={selectedAlternative}
                      data-tt={t('replan.pdfControls.select_older_version', {
                        defaultValue: 'Choose an older version of this page\nSelect from available alternatives\nSwitch to compare changes',
                      })}
                    >
                      {currentPageData.alternativeVersions.map(
                        (alt) => (
                          <option
                            key={`${alt.documentId}-${alt.actualPageNumber}`}
                            value={`${alt.version}|${alt.documentId}|${alt.actualPageNumber}`}
                          >
                            {alt.version} (
                            {alt.versionDate ||
                              t('replan.pdfControls.no_date')}
                            )
                          </option>
                        ),
                      )}
                    </select>
                  )}
              </label>

              {currentPageData &&
                currentPageData.alternativeVersions &&
                currentPageData.alternativeVersions.length > 1 && (
                  <div
                    style={{
                      fontSize: '0.85em',
                      borderTop: '1px solid var(--pico-muted-color)',
                      marginTop: '4px',
                      paddingTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        padding: '2px 8px',
                        color: 'var(--pico-muted-color)',
                        fontSize: '0.9em',
                      }}
                    >
                      {t('replan.pdfControls.show_shapes')}
                    </div>
                    {currentPageData.alternativeVersions.map(
                      (alt) => {
                        const isActive =
                          selectedVersionShapes.includes(
                            alt.version,
                          );
                        return (
                          <label
                            key={`shape-toggle-${alt.version}`}
                            className="version-option"
                            style={{
                              paddingLeft: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => {
                                const next = isActive
                                  ? selectedVersionShapes.filter(
                                    (v) => v !== alt.version,
                                  )
                                  : [
                                    ...selectedVersionShapes,
                                    alt.version,
                                  ];
                                if (onVersionShapesChange) {
                                  onVersionShapesChange(next);
                                }
                              }}
                            />
                            <span
                              style={{
                                opacity: isActive ? 1 : 0.5,
                              }}
                            >
                              {alt.version}
                            </span>
                          </label>
                        );
                      },
                    )}
                  </div>
                )}

              {overlayModeEnabled && (
                <>
                  {/* <label className="overlay-tint-control alt-version-select">
                  <input
                    type="checkbox"
                    checked={overlayFilterEnabled}
                    onChange={(e) => {
                      if (onOverlayFilterChange) {
                        onOverlayFilterChange(e.target.checked);
                      }
                    }}
                    data-tt={t('replan.pdfControls.enable_overlay_filter') || 'Enable color filter'}
                  />
                  {t('replan.pdfControls.overlay_filter') || 'Filter'}
                </label> */}
                  <label className="overlay-tint-control">
                    <input
                      type="checkbox"
                      checked={overlayFilterEnabled}
                      onChange={(e) => {
                        if (onOverlayFilterChange) {
                          onOverlayFilterChange(e.target.checked);
                        }
                      }}
                      data-tt={t('replan.pdfControls.overlay_filter_desc', {
                        defaultValue: 'Enable color filter on overlay pages\nOff by default\nMakes the overlaid version tinted',
                      })}
                    />
                    {t('replan.pdfControls.overlay_tint') || 'Tint'}
                    <input
                      type="color"
                      value={localOverlayTint}
                      onChange={(e) => {
                        const next = e.target.value;
                        setLocalOverlayTint(next);
                        if (onOverlayTintChange) {
                          onOverlayTintChange(next);
                        }
                      }}
                      disabled={!overlayFilterEnabled}
                      data-tt={t('replan.pdfControls.overlay_tint_desc', {
                        defaultValue: 'Pick a color to tint the overlay\nCurrent color shown in picker\nOnly active when filter is on',
                      })}
                    />
                  </label>
                  <div className="scale-switch version-mode-switch" style={{ marginTop: '5px', borderRadius: '5px' }}>
                    <button
                      type="button"
                      className={`switch-btn ${!isOverlayOnTop ? 'active' : ''}`}
                      onClick={() => {
                        if (onOverlayOrderChange) {
                          onOverlayOrderChange(false);
                        }
                      }}
                      data-tt={t('replan.pdfControls.background_on_top_desc', {
                        defaultValue: 'Show the background version on top\nCurrently active\nClick to toggle layer order',
                      })}
                    >
                      {t('replan.pdfControls.background_on_top') || 'Background'}
                    </button>
                    <button
                      type="button"
                      className={`switch-btn ${isOverlayOnTop ? 'active' : ''}`}
                      onClick={() => {
                        if (onOverlayOrderChange) {
                          onOverlayOrderChange(true);
                        }
                      }}
                      data-tt={t('replan.pdfControls.overlay_on_top_desc', {
                        defaultValue: 'Show the overlay version on top\nCurrently active\nClick to toggle layer order',
                      })}
                    >
                      {t('replan.pdfControls.overlay_on_top') || 'Overlay'}
                    </button>
                  </div>
                </>
              )}

              {/* <div className="version-actions">
              <button
                type="button"
                className="tiny-button"
                onClick={() => {
                  setSelectedVersions(allVersions);
                  onVersionChange('');
                }}
              >
                {t('replan.pdfControls.all')}
              </button>
              <button
                type="button"
                className="tiny-button"
                onClick={() => {
                  setSelectedVersions([]);
                  onVersionChange(allVersions.join(','));
                }}
              >
                {t('replan.pdfControls.none')}
              </button>
              <button
                type="button"
                className="tiny-button"
                onClick={handleVersionClose}
              >
                ❌
              </button>
            </div>

            <div className="version-options">
              {allVersions.map((version) => (
                <label key={version} className="version-option">
                  <input
                    type="checkbox"
                    checked={selectedVersions.includes(version)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newSelected = [
                          ...selectedVersions,
                          version,
                        ];
                        setSelectedVersions(newSelected);
                        const excluded = allVersions.filter(
                          (v) => !newSelected.includes(v),
                        );
                        onVersionChange(excluded.join(','));
                      } else {
                        const newSelected = selectedVersions.filter(
                          (v) => v !== version,
                        );
                        setSelectedVersions(newSelected);
                        const excluded = allVersions.filter(
                          (v) => !newSelected.includes(v),
                        );
                        onVersionChange(excluded.join(','));
                      }
                    }}
                  />
                  {version}
                </label>
              ))}
            </div> */}
            </div>,
            document.body,
          )}
      </div>
      <button
        onClick={prev_page}
        disabled={controlsDisabled}
        data-tt={t('replan.pdfControls.prev_page', {
          current: currentPage,
          total: numPages,
          defaultValue: 'Go to the previous page\nPage {{current}} of {{total}}\nCtrl+LeftArrow',
        })}
      >◀</button>
      <button
        onClick={next_page}
        disabled={controlsDisabled}
        data-tt={t('replan.pdfControls.next_page', {
          current: currentPage,
          total: numPages,
          defaultValue: 'Go to the next page\nPage {{current}} of {{total}}\nCtrl+RightArrow',
        })}
      >▶</button>

      <PageSelectorModal
        isOpen={isPageSelectorOpen}
        onClose={() => setIsPageSelectorOpen(false)}
        pages={allProjectPages}
        pagesLoading={projectPagesLoading}
        pagesError={projectPagesError}
        onRetryPages={() =>
          setProjectPagesReloadToken((token) => token + 1)
        }
        currentPage={currentPageData ? currentPageData.pageIndex + 1 : currentPage}
        onPageSelect={(newPage) => {
          handlePageChange(newPage);
          onResetView();
        }}
        pagesWithShapes={pagesWithShapes}
        displayKeys={displayKeys}
        displayUnitSystem={displayUnitSystem}
        authUser={authUser}
        firebase={firebase}
        projectName={projectName}
        measurementSystem={measurementSystem}
        selectedScaleKey={selectedScaleKey}
        currentScaleProps={currentScaleList.find(
          (scale) => scale.key === selectedScaleKey,
        )}
        // Props for filter controls in modal
        projects={[]}
        allVersions={allVersions}
        selectedVersions={selectedVersions}
        availableFolders={availableFolders}
        selectedFolder={selectedFolder}
        onFiltersApplied={(filters) => {
          // Apply folder change
          if (filters.folder !== selectedFolder && onFolderChange) {
            onFolderChange(filters.folder);
          }
          // Apply version changes
          if (filters.excludedVersions !== undefined) {
            if (filters.navigateTo) {
              onVersionChange(filters.excludedVersions, {
                navigateTo: filters.navigateTo,
                mode: 'page',
              });
            } else {
              onVersionChange(filters.excludedVersions);
            }
            // Update local selectedVersions based on excluded
            const excluded = filters.excludedVersions
              ? filters.excludedVersions.split(',')
              : [];
            const newSelected = allVersions.filter(
              (v) => !excluded.includes(v),
            );
            setSelectedVersions(newSelected);
          }
        }}
      />

      <div className="scale-selector" ref={scaleSelectorRef}>
        <button
          type="button"
          onClick={toggleScaleDropdown}
          disabled={controlsDisabled}
          className="dropdown-trigger"
          data-tt={t('replan.pdfControls.select_scale', {
            scale: selectedScaleLabel,
            defaultValue: 'Change measurement scale\nCurrent: {{scale}}\nClick to open selector',
          })}
        >
          {selectedScaleLabel} ▼
        </button>

        {isScaleDropdownOpen &&
          createPortal(
            <div
              ref={scaleDropdownRef}
              className="scale-dropdown-content"
              style={{
                top: `${scaleDropdownPos.top}px`,
                left: `${scaleDropdownPos.left}px`,
              }}
            >
              <div className="scale-switch">
                <button
                  className={`switch-btn ${measurementSystem === 'metric' ? 'active' : ''}`}
                  onClick={() =>
                    handleScaleSelect('metric', METRIC_SCALES[0].key)
                  }
                  type="button"
                >
                  {t('replan.pdfControls.metric')}
                </button>
                <button
                  className={`switch-btn ${measurementSystem === 'imperial' ? 'active' : ''}`}
                  onClick={() =>
                    handleScaleSelect(
                      'imperial',
                      IMPERIAL_SCALES[0].key,
                    )
                  }
                  type="button"
                >
                  {t('replan.pdfControls.imperial')}
                </button>
              </div>

              <select
                className="scale-select"
                style={{ borderRadius: '0' }}
                value={selectedScaleKey}
                onChange={(e) =>
                  onSelectedScaleKeyChange(e.target.value)
                }
                data-tt={t('replan.pdfControls.select_scale_desc', {
                  defaultValue: 'Choose a preset or custom scale\nApplies to measurements on the canvas\nSelect from the list below',
                })}
              >
                {currentScaleList.map((scale) => (
                  <option key={scale.key} value={scale.key}>
                    {scale.label}
                  </option>
                ))}
              </select>

              <div
                style={{
                  background:
                    'var(--pico-form-element-background-color)',
                  borderRadius:
                    '0 0 var(--pico-border-radius) var(--pico-border-radius)',
                }}
              >
                <button
                  onClick={() =>
                    setShowCustomScaleUI(!showCustomScaleUI)
                  }
                  className="scale-select"
                  style={{
                    background:
                      'var(--pico-form-element-background-color)',
                    border: 'none',
                    color: 'var(--pico-contrast)',
                    textAlign: 'left',
                    paddingLeft: '15px',
                  }}
                  data-tt={t('replan.pdfControls.custom_scale_desc', {
                    defaultValue: 'Define a custom drawing scale\nEnter ratio below and click + to add\nExample: 150 for 1:150',
                  })}
                >
                  {showCustomScaleUI ? '▼ ' : '▶ '}
                  {t('replan.pdfControls.custom_scale')}
                </button>

                {showCustomScaleUI && (
                  <div style={{ padding: '2px 8px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <input
                        type="number"
                        placeholder={t(
                          'replan.pdfControls.custom_scale_ratio',
                        )}
                        value={customScaleRatio}
                        onChange={(e) =>
                          setCustomScaleRatio(e.target.value)
                        }
                        min="1"
                        data-tt={t('replan.pdfControls.custom_scale_ratio_desc', {
                          defaultValue: 'Enter a numeric ratio for the scale\nExample: 150 for 1:150\nClick + to add it to your list',
                        })}
                        style={{ height: '2rem', margin: '0' }}
                      />
                      <button
                        onClick={handleAddCustomScale}
                        type="button"
                        data-tt={t('replan.pdfControls.custom_scale_add_desc', {
                          defaultValue: 'Add the entered ratio as a custom scale\nAppears in the scale selector dropdown\nClick + to confirm',
                        })}
                        style={{
                          height: '2rem',
                          width: '3rem',
                          padding: '0',
                        }}
                      >
                        +
                      </button>
                    </div>

                    {customScales.filter(
                      (cs) =>
                        cs.documentUnit ===
                        (measurementSystem === 'metric'
                          ? 'mm'
                          : 'in'),
                    ).length > 0 && (
                        <div>
                          <p
                            style={{
                              margin: '0',
                              fontSize: '0.85em',
                              color: 'var(--pico-contrast)',
                            }}
                          >
                            {t(
                              'replan.pdfControls.custom_scale_your_scales',
                            )}
                          </p>
                          {customScales
                            .filter(
                              (cs) =>
                                cs.documentUnit ===
                                (measurementSystem === 'metric'
                                  ? 'mm'
                                  : 'in'),
                            )
                            .map((scale) => (
                              <div
                                key={scale.key}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: '0.9em',
                                  padding: '4px 0',
                                }}
                              >
                                <span>{scale.label}</span>
                                <button
                                  onClick={() =>
                                    handleDeleteCustomScale(scale.key)
                                  }
                                  type="button"
                                  style={{
                                    padding: '0',
                                    lineHeight: '1',
                                    border: 'none',
                                    background: 'none',
                                    color: 'var(--pico-muted-color)',
                                    cursor: 'pointer',
                                  }}
                                  data-tt={t('replan.pdfControls.custom_scale_delete_desc', {
                                    defaultValue: 'Remove this custom scale permanently\nWill no longer appear in the selector\nThis cannot be undone',
                                  })}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )}
      </div>

      <div className="vertical-separator"></div>

      <input
        ref={searchInputRef}
        type="search"
        disabled={controlsDisabled}
        placeholder={t('replan.pdfControls.search_placeholder')}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          // Clear results when query changes
          if (e.target.value.length < 2) {
            setSearchResults([]);
            setCurrentMatchIndex(0);
            if (onSearchResults) {
              onSearchResults([], 0);
            }
          }
        }}
        onKeyDown={handleSearchKeyDown}
        className="search-input"
        data-tt={t('replan.pdfControls.search_nav', {
          current: currentMatchIndex + 1,
          total: searchResults.length,
          defaultValue: 'Searching the current page for text\nMatch {{current}} of {{total}}\nEnter for next, Esc to clear',
        })}
      />
      {searchResults.length > 0 && (
        <>
          <button
            onClick={handlePrevMatch}
            disabled={controlsDisabled}
            data-tt={t('replan.pdfControls.prev_match', {
              current: currentMatchIndex,
              total: searchResults.length,
              defaultValue: 'Go to the previous search match\nMatch {{current}} of {{total}}\nClick or use arrow keys',
            })}
            className="search-button"
          >
            ◄
          </button>
          {/* <span className="search-info" title="Search results">
            {currentMatchIndex + 1}/{searchResults.length}
          </span> */}
          <button
            onClick={handleNextMatch}
            disabled={controlsDisabled}
            data-tt={t('replan.pdfControls.next_match', {
              current: ((currentMatchIndex + 1) % searchResults.length) + 1,
              total: searchResults.length,
              defaultValue: 'Go to the next search match\nMatch {{current}} of {{total}}\nClick or use arrow keys',
            })}
            className="search-button"
          >
            ►
          </button>
        </>
      )}
      {isSearching && (
        <span data-tt={t('replan.pdfControls.searching_desc', {
          defaultValue: 'Searching the page for recognized text\nPlease wait while processing\nResults will appear automatically',
        })}>🔍</span>
      )}
      <div className="vertical-separator"></div>
      <button
        onClick={undo}
        disabled={!canUndo || controlsDisabled}
        data-tt={canUndo
          ? t('replan.pdfControls.undo', { defaultValue: 'Undo the last drawing action\nRevert the most recent change\nCtrl+Z' })
          : t('replan.pdfControls.undo_disabled', { defaultValue: 'Undo the last drawing action\nNothing to undo\nCtrl+Z when changes exist' })
        }
      >
        ↺
      </button>
      <button
        onClick={redo}
        disabled={!canRedo || controlsDisabled}
        data-tt={canRedo
          ? t('replan.pdfControls.redo', { defaultValue: 'Redo the last undone action\nRestore a previously undone change\nCtrl+Shift+Z' })
          : t('replan.pdfControls.redo_disabled', { defaultValue: 'Redo the last undone action\nNothing to redo\nCtrl+Shift+Z after undoing' })
        }
      >
        ↻
      </button>

      <button
        disabled={true}
        className="wide-button"
        data-tt={t('replan.pdfControls.zoom', {
          zoom: `${(stageScale * 100).toFixed(0)}%`,
          defaultValue: 'Current canvas zoom level\n{{zoom}}\nUse scroll wheel or pinch to adjust',
        })}
      >
        {(stageScale * 100).toFixed(0)}%
      </button>
      {/* <button onClick={onResetView} data-tt={t('replan.pdfControls.reset_view')}>
        &#128065;
      </button> */}
      {/* <button onClick={onPrint} data-tt={t('replan.pdfControls.print')}>
        🖨️
      </button> */}
            <button
        onClick={() => setIsShortcutModalOpen(true)}
        disabled={controlsDisabled}
        className="shortcut-help-btn"
        data-tt={`${t('replan.pdfControls.shortcut_help_desc', { defaultValue: 'View all keyboard shortcuts' })}\n${t('replan.pdfControls.shortcut_help')}\nShortcut: ?`}
        title="?"
      >
        ?
      </button>
      <ShortcutReferenceModal
        isOpen={isShortcutModalOpen}
        onClose={() => setIsShortcutModalOpen(false)}
      />
      <button
        onClick={onSave}
        disabled={saveStatus === 'saving' || controlsDisabled}
        data-tt={
          saveStatus === 'saving'
            ? t('replan.pdfControls.save_saving', { defaultValue: 'Saving your drawings to the server\nPlease wait while processing\nThis may take a moment' })
            : saveStatus === 'saved'
              ? t('replan.pdfControls.save_saved', { defaultValue: 'All drawings saved successfully\nNo pending changes\nCtrl+S to save again' })
              : t('replan.pdfControls.save_drawings', { defaultValue: 'Save current drawings to the server\nUnsaved changes present\nCtrl+S' })
        }
      >
        {saveStatus === 'saving'
          ? '⏳'
          : saveStatus === 'saved'
            ? '👍'
            : '💾'}
      </button>
      {showDiscardDraft && (
        <button
          onClick={onDiscardDraft}
          disabled={discardDraftStatus === 'deleting' || controlsDisabled}
          data-tt={
            discardDraftStatus === 'deleting'
              ? t('replan.pdfControls.discard_deleting', { defaultValue: 'Deleting the current draft\nPlease wait while processing\nReverting to last saved version' })
              : t('replan.pdfControls.discard_draft', { defaultValue: 'Discard all unsaved draft changes\nReverts to the last published version\nThis cannot be undone' })
          }
        >
          {discardDraftStatus === 'deleting' ? '⏳' : '🗑️'}
        </button>
      )}

    </div>
  );
};

export default PdfControls;
